import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { DefaultLink } from '@hivebrite/design-system/Link';

import { useMentorshipsContext } from 'hb-react/frontoffice/apps/Mentorships/contexts/MentorshipsContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import {
  CustomAttribute,
  Hidden,
} from 'hb-react/shared/components/formik/components/inputs';
import RequiredExplanation from 'hb-react/shared/components/formik/components/RequiredExplanation';
import { customAttributesInitialValues } from 'hb-react/shared/components/formik/utils/customAttributes';
import getCsrfToken from 'helpers/getCsrfToken';

const ApplyToBeMentorForm = ({
  onSubmit,
  onCancel,
  customAttributesList,
  customAttributeValues = [],
  isEditMode,
}) => {
  const { currentUser } = useDefaultAppContexts();
  const { mentorApplicationFormText } = useMentorshipsContext();

  const customAttributesListWithValues = customAttributesList.map(
    (customAttr) => {
      const customAttrValue =
        customAttributeValues?.length > 0
          ? customAttributeValues.find(
              (ca) => ca.custom_attr_id === customAttr.id,
            )?.attr_value
          : null;

      customAttr.value = customAttrValue;

      return customAttr;
    },
  );

  const initialValues = {
    authenticity_token: getCsrfToken(),
    mentorships_mentor: {
      request_message: '',
      ...customAttributesInitialValues(customAttributesListWithValues),
    },
  };

  return (
    <Box flexDirection="column" width="100%" alignItems="stretch">
      {!isEditMode && (
        <CustomMessageBox
          dangerouslySetInnerHTML={{
            __html: mentorApplicationFormText,
          }}
        />
      )}

      {isEditMode && (
        <Box pb={5}>
          {s__('Mentorships|Skills and Location are only editable from your')}
          <DefaultLink
            href={Routes.user_path(currentUser.id)}
            pl={1}
            styledOnlyOnHover
          >
            {s__('Mentorships|user profile')}
          </DefaultLink>
        </Box>
      )}

      <Box>
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
          {(formik) => (
            <Form style={{ width: '100%' }}>
              <ErrorFocus />
              <RequiredExplanation mt={0} mb={5} />

              <Hidden name="authenticity_token" />

              {customAttributesList.map((ca) => (
                <CustomAttribute
                  key={ca.id}
                  name={`mentorships_mentor[${ca.name}]`}
                  customAttribute={ca}
                />
              ))}

              <Box justifyContent="flex-end">
                {onCancel && (
                  <Box mr={4}>
                    <Button onClick={onCancel} variant="secondary">
                      {__('Cancel')}
                    </Button>
                  </Box>
                )}
                <Button disabled={formik.isSubmitting} type="submit">
                  {__('Submit')}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

ApplyToBeMentorForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  customAttributesList: PropTypes.array,
  customAttributeValues: PropTypes.object,
  isEditMode: PropTypes.bool,
};

ApplyToBeMentorForm.defaultProps = {
  customAttributeValues: {},
  isEditMode: false,
};

export default ApplyToBeMentorForm;

// Attempt at making the spacing consistent accross the dialog
// despite user-defined content.
const CustomMessageBox = styled(Box)`
  flex-direction: column;

  > p {
    margin-bottom: 12px;
  }
`;
